/**
 * Admin → Audit Log — recent admin actions (role/FA/active/test edits, roster
 * changes), newest first, from GET /admin/audit-log. Read-only trail so a
 * super_admin can see who changed what.
 */

import {
  Badge,
  Caption1,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import type { AuditLogRow } from '../types/admin'
import { useAuditLog } from '../hooks/useAdminUsers'
import { ErrorCard } from '../components/ErrorCard'

const ACTION_COLOR: Record<string, 'brand' | 'warning' | 'danger' | 'informative'> = {
  update_user: 'brand',
  bulk_update_user: 'brand',
  roster_add: 'informative',
  roster_restore: 'informative',
  roster_remove: 'warning',
}

/** Turn a {field: {from, to}} detail blob into a one-line summary. */
function summarize(detail: Record<string, unknown>): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(detail || {})) {
    if (v && typeof v === 'object' && 'to' in (v as object)) {
      const fromTo = v as { from?: unknown; to?: unknown }
      parts.push(`${k}: ${fromTo.from ?? '∅'} → ${fromTo.to ?? '∅'}`)
    } else {
      parts.push(`${k}: ${String(v)}`)
    }
  }
  return parts.join(' · ')
}

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  tableWrap: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  meta: { color: tokens.colorNeutralForeground3, fontSize: '12px' },
  empty: { padding: '48px 16px', textAlign: 'center', color: tokens.colorNeutralForeground3 },
})

export function AdminAuditLog() {
  const styles = useStyles()
  const { data, isLoading, isError, refetch } = useAuditLog(200)
  const rows: AuditLogRow[] = data ?? []

  return (
    <div className={styles.page}>
      <div>
        <Title2 block>Audit log</Title2>
      </div>

      {isLoading ? (
        <div className={styles.empty}>
          <Spinner size="small" />
        </div>
      ) : isError ? (
        <ErrorCard title="Could not load audit log" message="Please try again." retry={() => void refetch()} />
      ) : rows.length === 0 ? (
        <div className={styles.empty}>No admin actions recorded yet.</div>
      ) : (
        <div className={styles.tableWrap}>
          <Table aria-label="Audit log" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '16%' }}>When</TableHeaderCell>
                <TableHeaderCell style={{ width: '14%' }}>Action</TableHeaderCell>
                <TableHeaderCell style={{ width: '16%' }}>Actor</TableHeaderCell>
                <TableHeaderCell style={{ width: '16%' }}>Target</TableHeaderCell>
                <TableHeaderCell style={{ width: '38%' }}>Change</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Caption1>
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                    </Caption1>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={ACTION_COLOR[r.action] || 'informative'}>
                      {r.action}
                    </Badge>
                  </TableCell>
                  <TableCell>{r.actor_name || <span className={styles.meta}>—</span>}</TableCell>
                  <TableCell>{r.target_name || <span className={styles.meta}>—</span>}</TableCell>
                  <TableCell>
                    <Caption1>{summarize(r.detail) || '—'}</Caption1>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
